import { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Decimal from "decimal.js";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  AlertTitle,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { Close, Upload, CloudUpload } from "@mui/icons-material";
import { ExcelRenderer } from "react-excel-renderer";
import { importInvestmentsExcel } from "../../../../../actions/investmentsActions";

const InvestmentsExcelUpload = ({
  open,
  onClose,
  projectId,
  currentInvestments,
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.loading);

  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    setFile(null);
    setParsedData(null);
    setErrors([]);
    setWarnings([]);
    setShowPreview(false);
    onClose();
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) return;

    // Validar tipo de archivo
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      setErrors([
        "Tipo de archivo inválido. Por favor selecciona un archivo Excel (.xls o .xlsx)",
      ]);
      return;
    }

    // Validar tamaño (máximo 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setErrors([
        "El archivo es demasiado grande. El tamaño máximo permitido es 5MB",
      ]);
      return;
    }

    setFile(selectedFile);
    setErrors([]);
    parseExcelFile(selectedFile);
  };

  const parseExcelFile = (file) => {
    setIsValidating(true);

    ExcelRenderer(file, (err, resp) => {
      if (err) {
        setErrors(["Error al leer el archivo Excel"]);
        setIsValidating(false);
        return;
      }

      // Validar que tenga datos
      if (!resp.rows || resp.rows.length < 2) {
        setErrors(["El archivo está vacío o no tiene datos válidos"]);
        setIsValidating(false);
        return;
      }

      // Parsear los datos
      const headers = resp.rows[0];
      const dataRows = resp.rows.slice(1);

      // Validar headers esperados
      const requiredHeaders = [
        "ID Inversión (NO EDITAR)",
        "Asociado (NO EDITAR)",
        "Nombre Inversión",
        "Vehículo",
        "Capital",
        "Participación Global (%)",
        "Participación IDE (%)",
      ];

      const missingHeaders = requiredHeaders.filter(
        (header) => !headers.includes(header)
      );

      if (missingHeaders.length > 0) {
        setErrors([`Faltan columnas requeridas: ${missingHeaders.join(", ")}`]);
        setIsValidating(false);
        return;
      }

      // Obtener índices de columnas
      const getColumnIndex = (headerName) => headers.indexOf(headerName);

      const idIndex = getColumnIndex("ID Inversión (NO EDITAR)");
      const associateIndex = getColumnIndex("Asociado (NO EDITAR)");
      const investorIndex = getColumnIndex("Nombre Inversión");
      const vehiculoIndex = getColumnIndex("Vehículo");
      const capitalIndex = getColumnIndex("Capital");
      const participationIndex = getColumnIndex("Participación Global (%)");
      const participationIDEIndex = getColumnIndex("Participación IDE (%)");

      // Parsear y validar cada fila
      const validationErrors = [];
      const investments = [];

      dataRows.forEach((row, index) => {
        // Saltar filas vacías
        if (!row || row.every((cell) => !cell)) return;

        const rowNumber = index + 2; // +2 porque índice empieza en 0 y hay header
        const rowErrors = [];

        const investment = {
          _id: row[idIndex],
          associateName: row[associateIndex],
          investor: row[investorIndex],
          vehiculo: row[vehiculoIndex],
          capital: parseFloat(row[capitalIndex]) || 0,
          participation: parseFloat(row[participationIndex]) || 0,
          participationIDE: parseFloat(row[participationIDEIndex]) || 0,
          errors: [], // Array para almacenar errores específicos de esta inversión
        };

        // Validaciones
        if (!investment._id) {
          rowErrors.push("Falta ID de inversión");
          validationErrors.push(`Fila ${rowNumber}: Falta el ID de inversión`);
        }

        if (!investment.associateName) {
          rowErrors.push("Falta nombre del asociado");
          validationErrors.push(
            `Fila ${rowNumber}: Falta el nombre del asociado`
          );
        }

        if (!investment.investor) {
          rowErrors.push("Falta nombre de inversión");
          validationErrors.push(
            `Fila ${rowNumber}: Falta el nombre de inversión`
          );
        }

        if (investment.capital < 0) {
          rowErrors.push("Capital negativo");
          validationErrors.push(
            `Fila ${rowNumber}: El capital no puede ser negativo`
          );
        }

        if (investment.participation < 0 || investment.participation > 100) {
          rowErrors.push("Participación global fuera de rango (0-100)");
          validationErrors.push(
            `Fila ${rowNumber}: La participación global debe estar entre 0 y 100`
          );
        }

        if (
          investment.participationIDE < 0 ||
          investment.participationIDE > 100
        ) {
          rowErrors.push("Participación IDE fuera de rango (0-100)");
          validationErrors.push(
            `Fila ${rowNumber}: La participación IDE debe estar entre 0 y 100`
          );
        }

        investment.errors = rowErrors;
        investments.push(investment);
      });

      // Validar IDs duplicados
      const ids = investments.map((inv) => inv._id);
      const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);

      if (duplicateIds.length > 0) {
        const uniqueDuplicates = [...new Set(duplicateIds)];

        // Marcar las inversiones con IDs duplicados
        investments.forEach((inv) => {
          if (uniqueDuplicates.includes(inv._id)) {
            inv.errors.push("ID duplicado");
          }
        });

        validationErrors.push(
          `IDs duplicados encontrados: ${uniqueDuplicates.join(
            ", "
          )}. Cada inversión debe tener un ID único.`
        );
      }

      // Validar que la suma de participaciones sea 100%
      // TODO: Descomentar cuando se requiera validar el 100%
      // const totalParticipation = investments.reduce(
      //   (sum, inv) => sum + inv.participation,
      //   0
      // );

      // const tolerance = 0.01;
      // if (Math.abs(totalParticipation - 100) > tolerance) {
      //   validationErrors.push(
      //     `La suma total de participaciones es ${totalParticipation.toFixed(
      //       2
      //     )}%. Debe ser exactamente 100.00% (tolerancia ±0.01%)`
      //   );
      // }

      // Comparar con las inversiones actuales para detectar cambios
      const validationWarnings = [];
      const investmentsWithChanges = [];

      investments.forEach((investment) => {
        const currentInv = currentInvestments.find(
          (inv) => inv._id === investment._id
        );

        if (!currentInv) {
          // Inversión no existe en la lista actual - esto es un error
          investment.errors.push("ID no encontrado");
          validationErrors.push(
            `ID ${investment._id} no existe en la lista actual de inversiones`
          );
        } else {
          // Comparar si hay cambios reales
          const hasChanges =
            currentInv.investor !== investment.investor ||
            currentInv.vehiculo !== investment.vehiculo ||
            currentInv.capital !== investment.capital ||
            currentInv.participation !== investment.participation ||
            currentInv.participationIDE !== investment.participationIDE;

          if (!hasChanges) {
            investment.warnings = ["Sin cambios"];
          } else {
            investment.hasChanges = true;
            investmentsWithChanges.push(investment);
          }
        }
      });

      // Si no hay inversiones con cambios, agregar warning global
      if (
        investmentsWithChanges.length === 0 &&
        validationErrors.length === 0
      ) {
        validationWarnings.push(
          "No hay cambios para aplicar. Todas las inversiones tienen los mismos valores que los actuales."
        );
      }

      // Siempre mostrar la tabla con los datos parseados
      setParsedData(investments);
      setErrors(validationErrors);
      setWarnings(validationWarnings);
      setShowPreview(true);
      setIsValidating(false);
    });
  };

  const handleConfirm = () => {
    if (!parsedData || errors.length > 0) return;

    // Filtrar solo las inversiones que tienen cambios
    const investmentsWithChanges = parsedData.filter((inv) => inv.hasChanges);

    // Si no hay cambios, no hacer nada
    if (investmentsWithChanges.length === 0) {
      return;
    }

    // Preparar datos para el backend según el formato esperado
    const dataToImport = investmentsWithChanges.map((investment) => ({
      _id: investment._id,
      investor: investment.investor,
      vehiculo: investment.vehiculo,
      capital: investment.capital,
      participation: investment.participation,
      participationIDE: investment.participationIDE,
    }));

    dispatch(
      importInvestmentsExcel({
        projectId,
        data: dataToImport,
        query: { projectRef: projectId, results: 99999 }, // al update refetch all investments with a high limit
        callback: () => {
          handleClose();
        },
      })
    );
  };

  const totalParticipation = parsedData
    ? parsedData
        .reduce(
          (sum, inv) => sum.plus(new Decimal(inv.participation)),
          new Decimal(0)
        )
        .toNumber()
    : 0;

  const totalParticipationIDE = parsedData
    ? parsedData
        .reduce(
          (sum, inv) => sum.plus(new Decimal(inv.participationIDE)),
          new Decimal(0)
        )
        .toNumber()
    : 0;

  // Contar inversiones con cambios
  const investmentsWithChangesCount = parsedData
    ? parsedData.filter((inv) => inv.hasChanges).length
    : 0;

  // Validación: no errores Y hay al menos una inversión con cambios
  const isValid =
    parsedData && errors.length === 0 && investmentsWithChangesCount > 0;

  // Validación para participaciones IDE no se mayor al 100%
  const isValidIDE = totalParticipationIDE <= 100;

  // TODO: Descomentar cuando se requiera validar el 100%
  // const isValid =
  //   parsedData &&
  //   errors.length === 0 &&
  //   Math.abs(totalParticipation - 100) <= 0.01;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5">
            Actualización masiva de inversiones
          </Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 2 }} />
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {!showPreview ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xls,.xlsx"
                style={{ display: "none" }}
                onChange={handleFileSelect}
              />
              <CloudUpload
                sx={{ fontSize: 64, color: "action.disabled", mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                Selecciona un archivo Excel
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                El archivo debe contener las columnas: ID Inversión, Asociado,
                Nombre Inversión, Vehículo, Capital, Participación Global (%),
                Participación IDE (%)
              </Typography>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={() => fileInputRef.current?.click()}
              >
                Seleccionar Archivo
              </Button>
              <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                Tamaño máximo: 5MB | Formatos: .xls, .xlsx
              </Typography>
            </Box>
          ) : (
            <Box>
              <Alert
                severity={
                  errors.length > 0
                    ? "error"
                    : warnings.length > 0
                    ? "warning"
                    : "info"
                }
                sx={{ mb: 2 }}
              >
                <AlertTitle>
                  {errors.length > 0
                    ? "Se encontraron errores de validación"
                    : warnings.length > 0
                    ? "Advertencias"
                    : "Preview de cambios"}
                </AlertTitle>
                {errors.length > 0 ? (
                  <Typography variant="body2">
                    Revisa la columna "Estado" en la tabla para ver los errores
                    específicos de cada fila. Corrige los errores en el archivo
                    Excel y vuelve a cargarlo.
                  </Typography>
                ) : warnings.length > 0 ? (
                  <Typography variant="body2">
                    Algunas inversiones no tienen cambios o no fueron
                    encontradas. Solo se actualizarán las inversiones con
                    cambios válidos.
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    Revisa los datos antes de aplicar los cambios. Todos los
                    registros pasaron las validaciones correctamente.
                  </Typography>
                )}
              </Alert>

              {file && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Archivo:</strong> {file.name}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Total de registros:</strong>{" "}
                    {parsedData?.length || 0}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Con cambios:</strong>{" "}
                    {parsedData?.filter((inv) => inv.hasChanges).length || 0}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Suma de participaciones Global:</strong>
                    </Typography>
                    <Chip
                      label={`${totalParticipation.toFixed(2)}%`}
                      color={isValid ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2">
                      <strong>Suma de participaciones IDE:</strong>
                    </Typography>
                    <Chip
                      label={`${totalParticipationIDE.toFixed(2)}%`}
                      color={isValidIDE ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                </Box>
              )}

              <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          fontWeight: "bold",
                          backgroundColor: "grey.100",
                          color: "black",
                        },
                      }}
                    >
                      <TableCell>#</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Asociado</TableCell>
                      <TableCell>Nombre Inversión</TableCell>
                      <TableCell>Vehículo</TableCell>
                      <TableCell align="right">Capital</TableCell>
                      <TableCell align="right">Part. Global (%)</TableCell>
                      <TableCell align="right">Part. IDE (%)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parsedData?.map((investment, index) => {
                      const hasErrors =
                        investment.errors && investment.errors.length > 0;
                      const hasWarnings =
                        investment.warnings && investment.warnings.length > 0;

                      return (
                        <TableRow
                          key={investment._id}
                          sx={{
                            backgroundColor: hasErrors
                              ? "rgba(211, 47, 47, 0.08)"
                              : hasWarnings
                              ? "rgba(237, 108, 2, 0.08)"
                              : "transparent",
                            "&:hover": {
                              backgroundColor: hasErrors
                                ? "rgba(211, 47, 47, 0.12)"
                                : hasWarnings
                                ? "rgba(237, 108, 2, 0.12)"
                                : "action.hover",
                            },
                          }}
                        >
                          <TableCell
                            sx={{ width: 25, minWidth: 25, maxWidth: 25 }}
                          >
                            {index + 1}
                          </TableCell>
                          <TableCell
                            sx={{ width: 120, minWidth: 120, maxWidth: 120 }}
                          >
                            {hasErrors ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                {investment.errors.map((error, idx) => (
                                  <Chip
                                    key={idx}
                                    label={error}
                                    color="error"
                                    size="small"
                                  />
                                ))}
                              </Box>
                            ) : hasWarnings ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 0.5,
                                }}
                              >
                                {investment.warnings.map((warning, idx) => (
                                  <Chip
                                    key={idx}
                                    label={warning}
                                    color="warning"
                                    size="small"
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Chip label="OK" color="success" size="small" />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={investment._id}
                          >
                            {investment?._id?.toString().substring(0, 5) || ""}
                            ...
                          </TableCell>
                          <TableCell>{investment.associateName}</TableCell>
                          <TableCell>{investment.investor}</TableCell>
                          <TableCell>{investment.vehiculo}</TableCell>
                          <TableCell align="right">
                            ${investment.capital.toLocaleString()}
                          </TableCell>
                          <TableCell align="right">
                            {investment.participation.toFixed(2)}%
                          </TableCell>
                          <TableCell align="right">
                            {investment.participationIDE.toFixed(2)}%
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}

          {errors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <AlertTitle>Errores de validación</AlertTitle>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                {errors.map((error, index) => (
                  <li key={index}>
                    <Typography variant="body2">{error}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {warnings.length > 0 && errors.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>Advertencias</AlertTitle>
              <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                {warnings.map((warning, index) => (
                  <li key={index}>
                    <Typography variant="body2">{warning}</Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}

          {(loading || isValidating) && <LinearProgress sx={{ mt: 2 }} />}
        </Box>
      </DialogContent>

      <DialogActions>
        {showPreview && (
          <Button
            onClick={() => {
              setShowPreview(false);
              setParsedData(null);
              setFile(null);
              setErrors([]);
              setWarnings([]);
            }}
            disabled={loading}
          >
            Volver a seleccionar
          </Button>
        )}
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        {showPreview && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!isValid || loading}
          >
            {loading ? "Aplicando..." : "Confirmar y Aplicar Cambios"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InvestmentsExcelUpload;
